import clsx from "clsx";
import { motion } from "motion/react";
import { useRef } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "../localization";

/** Props Interface */
interface TextInputAddTriggerProps<T extends FieldValues> {
	methods: UseFormReturn<T>;
	path: Path<T>;
}

export function TextInputAddTrigger<T extends FieldValues>({
	methods,
	path,
}: TextInputAddTriggerProps<T>) {
	/** Hooks */
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);

	/** Functions */
	function handleOnKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		const input = event.currentTarget;
		const inputValue = event.key;
		if (inputValue.length > 1) return;
		const array = methods.getValues(path) as string[];
		if (typeof array.at(0) !== typeof inputValue) {
			methods.setValue(path, [inputValue] as any);
		} else {
			methods.setValue(path, [...array, inputValue] as any);
		}
		requestAnimationFrame(() => {
			focusPreviousInput();
			input.value = "";
		});
	}

	function focusPreviousInput() {
		const containerElement = inputRef.current?.parentElement;
		const inputs = containerElement?.querySelectorAll("input[type='text']");
		if (!inputs) return;
		const previousInput = Array.from(inputs).at(-1) as HTMLInputElement;
		previousInput?.focus();
	}

	/** Render */
	return (
		<motion.input
			ref={inputRef}
			className={clsx("input", "opacity-50 border-dashed")}
			placeholder={t("placeholders.type_to_add")}
			onKeyDown={handleOnKeyDown}
		/>
	);
}
